// mcn-frontend/src/components/auth/Login.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import HoneypotField from '../common/HoneypotField';
import './Auth.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://svc.choicemcn.com';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const navigate = useNavigate();
  const { login } = useUser();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...formData, website: honeypot })
      });

      const data = await response.json();

      if (response.ok) {
        // 2FA gate - if the user has 2FA enabled, redirect to the OTP challenge
        if (data.twoFactorRequired) {
          sessionStorage.setItem('twofa.pendingToken', data.pendingToken);
          sessionStorage.setItem('twofa.method', data.method);
          sessionStorage.setItem('twofa.maskedEmail', data.maskedEmail || '');
          navigate('/login-2fa');
          return;
        }
        // Store token and tenant info
       // Store both for compatibility
localStorage.setItem('token', data.accessToken);  // Keep for old components
localStorage.setItem('accessToken', data.accessToken);  // Add for new dashboard
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Store tenant information if available
        if (data.user.tenant) {
          localStorage.setItem('tenantId', data.user.tenant.id);
          localStorage.setItem('tenantName', data.user.tenant.name);
        }
        
        // Update user context with full user data including tenant
        login(data.user);
        
        // Enhanced role-based routing with TENANT_ADMIN
        switch(data.user.role) {
          case 'SUPER_ADMIN':
            navigate('/super-admin-dashboard');
            break;
          case 'TENANT_ADMIN':
            navigate('/tenant-admin-dashboard');
            break;
          case 'ADMIN':
            navigate('/admin-dashboard');
            break;
          case 'MANAGER':
          case 'MCN_MANAGER':
            navigate('/manager-dashboard');
            break;
          case 'FINANCE_MANAGER':
            navigate('/finance-dashboard');
            break;
          case 'CONTENT_MANAGER':
            navigate('/content-moderation');
            break;
          case 'AGGREGATOR':
            navigate('/aggregator-dashboard');
            break;
          case 'CHANNEL_OWNER':
            navigate('/channel-dashboard');
            break;
          case 'CREATOR':
          case 'CONTENT_CREATOR':
            navigate('/creator-dashboard');
            break;
          case 'STUDIO_ADMIN':
          case 'STUDIO_MEMBER':
          case 'STUDIO':
            navigate('/studio-dashboard');
            break;
          case 'INVESTOR':
          case 'PREMIUM_INVESTOR':
            navigate('/investor-dashboard');
            break;
          case 'SUPPORT_AGENT':
          case 'SUPPORT':
            navigate('/support-dashboard');
            break;
          case 'ANALYTICS_VIEWER':
            navigate('/analytics-dashboard');
            break;
          case 'VIEWER':
          case 'MODERATOR':
          case 'USER':
            navigate('/dashboard');
            break;
          default:
            console.log('Unknown role:', data.user.role);
            navigate('/dashboard');
        }
      } else {
        setError(data.message || data.error || 'Invalid email or password');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Quick login buttons for development
  const quickLogin = (email, password) => {
    setFormData({ email, password });
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Login to Choice Network</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email"
              autoComplete="email"
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Enter your password"
              autoComplete="current-password"
            />
          </div>
          <HoneypotField value={honeypot} onChange={setHoneypot} />
          {error && <div className="error-message">{error}</div>}
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        
        {/* Development Quick Login - Remove in Production */}
        {process.env.NODE_ENV === 'development' && (
          <div className="quick-login-dev" style={{ marginTop: '20px', padding: '10px', border: '1px dashed #ccc' }}>
            <p style={{ fontSize: '12px', color: '#666' }}>Quick Login (Dev Only):</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '10px' }}>
              <button 
                type="button"
                onClick={() => quickLogin('superadmin@test.com', 'password123')}
                style={{ fontSize: '11px', padding: '5px 10px' }}
                className="quick-login-btn"
              >
                Super Admin
              </button>
              <button 
                type="button"
                onClick={() => quickLogin('tenantadmin@test.com', 'password123')}
                style={{ fontSize: '11px', padding: '5px 10px' }}
                className="quick-login-btn"
              >
                Tenant Admin
              </button>
              <button 
                type="button"
                onClick={() => quickLogin('admin@test.com', 'password123')}
                style={{ fontSize: '11px', padding: '5px 10px' }}
                className="quick-login-btn"
              >
                Admin
              </button>
              <button 
                type="button"
                onClick={() => quickLogin('manager@choicenetwork.in', 'password123')}
                style={{ fontSize: '11px', padding: '5px 10px' }}
                className="quick-login-btn"
              >
                Manager
              </button>
              <button 
                type="button"
                onClick={() => quickLogin('aggregator.mumbai@choicenetwork.in', 'password123')}
                style={{ fontSize: '11px', padding: '5px 10px' }}
                className="quick-login-btn"
              >
                Aggregator
              </button>
              <button 
                type="button"
                onClick={() => quickLogin('healthyliving365@choicenetwork.in', 'password123')}
                style={{ fontSize: '11px', padding: '5px 10px' }}
                className="quick-login-btn"
              >
                Channel Owner
              </button>
              <button 
                type="button"
                onClick={() => quickLogin('creator@test.com', 'password123')}
                style={{ fontSize: '11px', padding: '5px 10px' }}
                className="quick-login-btn"
              >
                Creator
              </button>
              <button 
                type="button"
                onClick={() => quickLogin('investor@test.com', 'password123')}
                style={{ fontSize: '11px', padding: '5px 10px' }}
                className="quick-login-btn"
              >
                Investor
              </button>
            </div>
          </div>
        )}
        
        <p className="auth-link">
          Don't have an account? <a href="/register">Register here</a>
        </p>
        <p className="auth-link" style={{ marginTop: '10px', fontSize: '12px' }}>
          <a href="/forgot-password">Forgot Password?</a>
        </p>
      </div>
    </div>
  );
};

export default Login;